import { DashboardData, RawDataItem } from '../../../types';
import {
  NON_COMPLIANCE_RESULT,
  UNFILLED_REASON_LABEL,
  normalizeField,
  normalizeReason,
  orderedResultTypes,
  recordWeight,
} from './dataProcessing';

export type XAxisGranularity = 'month' | 'quarter' | 'year' | 'fiscalYear';

export type TimeSeriesMetric =
  | 'resultBreakdown'
  | 'nonComplianceReasonBreakdown'
  | 'nonComplianceOccupationBreakdown';

export const INCLUDE_UNFILLED_REASON_IN_TIMESERIES = true;
export const NON_COMPLIANCE_OCCUPATION_TOP_N = 8;
export const OTHER_OCCUPATION_LABEL = 'その他';
const RATIO_DECIMAL_PLACES = 1;

export type TimeSeriesBucketRow = {
  label: string;
  startDate: Date;
  total: number;
} & Record<string, string | number | Date>;

type BucketAccumulator = {
  label: string;
  startDate: Date;
  total: number;
  resultCounts: Record<string, number>;
};

const buildBucketDisplayRow = (
  keys: string[],
  bucket: Omit<BucketAccumulator, 'resultCounts'>,
  resultCounts: Record<string, number>
): TimeSeriesBucketRow => {
  const ratioEntry = buildDisplayRatioEntry(keys, resultCounts, bucket.total);

  return {
    ...bucket,
    ...ratioEntry,
  };
};

const toPaddedMonth = (monthIndex: number) => String(monthIndex + 1).padStart(2, '0');

export const addMonthsClamped = (date: Date, months: number) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  const targetMonthIndex = month + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const clampedDay = Math.min(day, lastDayOfTargetMonth);

  return new Date(
    targetYear,
    targetMonth,
    clampedDay,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
};

export const isDateRangeAtLeastTwoMonths = (dates: Date[]) => {
  if (dates.length === 0) return false;

  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
  const firstDate = sortedDates[0];
  const lastDate = sortedDates[sortedDates.length - 1];

  const thresholdDate = addMonthsClamped(firstDate, 2);

  return lastDate >= thresholdDate;
};

export const normalizeRawDataDate = (value?: RawDataItem['date']): Date | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const getTimeBucketKey = (date: Date, granularity: XAxisGranularity): string => {
  const year = date.getFullYear();

  if (granularity === 'fiscalYear') {
    const fiscalYear = date.getMonth() >= 3 ? year : year - 1;
    return `${fiscalYear}年度`;
  }

  if (granularity === 'year') {
    return String(year);
  }

  if (granularity === 'quarter') {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `${year}-Q${quarter}`;
  }

  return `${year}-${toPaddedMonth(date.getMonth())}`;
};

const getBucketStartDate = (date: Date, granularity: XAxisGranularity): Date => {
  const year = date.getFullYear();

  if (granularity === 'fiscalYear') {
    const fiscalYear = date.getMonth() >= 3 ? year : year - 1;
    return new Date(fiscalYear, 3, 1);
  }

  if (granularity === 'year') {
    return new Date(year, 0, 1);
  }

  if (granularity === 'quarter') {
    const month = Math.floor(date.getMonth() / 3) * 3;
    return new Date(year, month, 1);
  }

  return new Date(year, date.getMonth(), 1);
};

const createEmptyResultBreakdown = () => {
  return orderedResultTypes.reduce<Record<string, number>>((acc, resultType) => {
    acc[resultType] = 0;
    return acc;
  }, {});
};

const getRoundedUnits = (value: number, decimalPlaces: number) => {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor);
};

export const buildDisplayRatioEntry = (
  keys: string[],
  counts: Record<string, number>,
  total: number,
  decimalPlaces: number = RATIO_DECIMAL_PLACES
) => {
  const ratioEntry = keys.reduce<Record<string, number>>((acc, key) => {
    acc[key] = 0;
    acc[`${key}Count`] = counts[key] || 0;
    return acc;
  }, {});

  if (total <= 0 || keys.length === 0) {
    return ratioEntry;
  }

  const factor = 10 ** decimalPlaces;
  const targetUnits = 100 * factor;
  const ratioUnits = keys.map((key) => {
    const count = counts[key] || 0;
    return getRoundedUnits((count / total) * 100, decimalPlaces);
  });

  let diffUnits = targetUnits - ratioUnits.reduce((sum, units) => sum + units, 0);
  const maxIndex = ratioUnits.reduce((maxIdx, units, idx, arr) =>
    units > arr[maxIdx] ? idx : maxIdx, 0);

  if (diffUnits >= 0) {
    ratioUnits[maxIndex] += diffUnits;
  } else {
    let currentIndex = maxIndex;

    while (diffUnits < 0 && currentIndex >= 0) {
      const deduction = Math.min(ratioUnits[currentIndex], -diffUnits);
      ratioUnits[currentIndex] -= deduction;
      ratioUnits[currentIndex] = Math.max(0, ratioUnits[currentIndex]);
      diffUnits += deduction;

      if (diffUnits < 0) {
        currentIndex -= 1;
      }
    }
  }

  keys.forEach((key, index) => {
    ratioEntry[key] = ratioUnits[index] / factor;
  });

  return ratioEntry;
};

export const aggregateTimeSeriesByResult = (
  rawData: DashboardData['raw_data'],
  granularity: XAxisGranularity,
  options?: {
    allowShortRange?: boolean;
  }
): TimeSeriesBucketRow[] => {
  const dates = rawData
    .map(item => normalizeRawDataDate(item.date))
    .filter((date): date is Date => !!date);

  if (!options?.allowShortRange && !isDateRangeAtLeastTwoMonths(dates)) {
    return [];
  }

  const buckets = new Map<string, BucketAccumulator>();

  rawData.forEach((item) => {
    const normalizedDate = normalizeRawDataDate(item.date);
    if (!normalizedDate) return;

    if (!item.result || !orderedResultTypes.includes(item.result)) {
      return;
    }

    const bucketKey = getTimeBucketKey(normalizedDate, granularity);
    const bucketStartDate = getBucketStartDate(normalizedDate, granularity);

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        label: bucketKey,
        startDate: bucketStartDate,
        total: 0,
        resultCounts: createEmptyResultBreakdown(),
      });
    }

    const bucket = buckets.get(bucketKey);
    if (!bucket) return;

    const weight = recordWeight(item);
    bucket.total += weight;
    bucket.resultCounts[item.result] += weight;
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .map(({ resultCounts, ...bucket }) => buildBucketDisplayRow(orderedResultTypes, bucket, resultCounts));
};

export const buildSeriesByMetric = (
  metric: TimeSeriesMetric,
  granularity: XAxisGranularity,
  data: DashboardData,
  options?: {
    allowShortRange?: boolean;
  }
) => {
  const dates = (data.time_series_data || data.raw_data || [])
    .map(record => normalizeRawDataDate(record.date))
    .filter((date): date is Date => !!date);

  const insufficientRange = !isDateRangeAtLeastTwoMonths(dates);
  const shouldSkipByRange = insufficientRange && !options?.allowShortRange;

  if (shouldSkipByRange) {
    return {
      series: [] as TimeSeriesBucketRow[],
      seriesKeys: [] as string[],
      insufficientRange,
    };
  }

  const rawData = data.time_series_data || data.raw_data || [];

  switch (metric) {
    case 'nonComplianceReasonBreakdown': {
      const { series, keys } = aggregateTimeSeriesByNonComplianceReason(
        rawData,
        granularity,
        INCLUDE_UNFILLED_REASON_IN_TIMESERIES
      );

      return {
        series,
        seriesKeys: keys,
        insufficientRange,
      };
    }
    case 'nonComplianceOccupationBreakdown': {
      const { series, keys } = aggregateTimeSeriesByNonComplianceOccupation(
        rawData,
        granularity,
        NON_COMPLIANCE_OCCUPATION_TOP_N
      );

      return {
        series,
        seriesKeys: keys,
        insufficientRange,
      };
    }
    case 'resultBreakdown':
    default:
      return {
        series: aggregateTimeSeriesByResult(rawData, granularity, {
          allowShortRange: options?.allowShortRange,
        }),
        // 結果内訳は orderedResultTypes に含まれる結果のみを母集団として扱う．
        seriesKeys: orderedResultTypes,
        insufficientRange,
      };
  }
};

const aggregateTimeSeriesByNonComplianceReason = (
  rawData: DashboardData['raw_data'],
  granularity: XAxisGranularity,
  includeUnfilledReason: boolean
) => {
  const buckets = new Map<string, BucketAccumulator>();
  const reasonSet = new Set<string>();

  rawData.forEach((item) => {
    if (item.result !== NON_COMPLIANCE_RESULT) return;

    const normalizedDate = normalizeRawDataDate(item.date);
    if (!normalizedDate) return;

    const reason = normalizeReason(item.reason);
    if (!includeUnfilledReason && reason === UNFILLED_REASON_LABEL) return;

    const bucketKey = getTimeBucketKey(normalizedDate, granularity);
    const bucketStartDate = getBucketStartDate(normalizedDate, granularity);

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        label: bucketKey,
        startDate: bucketStartDate,
        total: 0,
        resultCounts: {},
      });
    }

    const bucket = buckets.get(bucketKey);
    if (!bucket) return;

    const weight = recordWeight(item);
    bucket.total += weight;
    bucket.resultCounts[reason] = (bucket.resultCounts[reason] || 0) + weight;
    reasonSet.add(reason);
  });

  const keys = Array.from(reasonSet).sort((a, b) => {
    if (a === UNFILLED_REASON_LABEL) return 1;
    if (b === UNFILLED_REASON_LABEL) return -1;
    return a.localeCompare(b, 'ja');
  });

  const series = Array.from(buckets.values())
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .map(({ resultCounts, ...bucket }) => buildBucketDisplayRow(keys, bucket, resultCounts));

  return { series, keys };
};

const aggregateTimeSeriesByNonComplianceOccupation = (
  rawData: DashboardData['raw_data'],
  granularity: XAxisGranularity,
  topN: number
) => {
  const occupationTotals: Record<string, number> = {};

  rawData.forEach((item) => {
    if (item.result !== NON_COMPLIANCE_RESULT) return;
    const occupation = normalizeField(item.occupation);
    occupationTotals[occupation] = (occupationTotals[occupation] || 0) + recordWeight(item);
  });

  const sortedOccupations = Object.entries(occupationTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([occupation]) => occupation);

  const primaryOccupations = sortedOccupations.slice(0, topN);
  const hasOthers = sortedOccupations.length > topN;
  const keys = hasOthers ? [...primaryOccupations, OTHER_OCCUPATION_LABEL] : primaryOccupations;
  const occupationKeySet = new Set(primaryOccupations);

  const buckets = new Map<string, BucketAccumulator>();

  rawData.forEach((item) => {
    if (item.result !== NON_COMPLIANCE_RESULT) return;

    const normalizedDate = normalizeRawDataDate(item.date);
    if (!normalizedDate) return;

    const bucketKey = getTimeBucketKey(normalizedDate, granularity);
    const bucketStartDate = getBucketStartDate(normalizedDate, granularity);

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        label: bucketKey,
        startDate: bucketStartDate,
        total: 0,
        resultCounts: {},
      });
    }

    const bucket = buckets.get(bucketKey);
    if (!bucket) return;

    const occupation = normalizeField(item.occupation);
    const groupedOccupation = occupationKeySet.has(occupation) ? occupation : OTHER_OCCUPATION_LABEL;

    const weight = recordWeight(item);
    bucket.total += weight;
    bucket.resultCounts[groupedOccupation] =
      (bucket.resultCounts[groupedOccupation] || 0) + weight;
  });

  const series = Array.from(buckets.values())
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .map(({ resultCounts, ...bucket }) => buildBucketDisplayRow(keys, bucket, resultCounts));

  return { series, keys };
};
